(ns app.front.view.admin.entity-table
  "A shared table for displaying formatted entity data for entity lookup and audit log"
  (:require [app.front.clipboard :as clipboard]
            [app.front.util :refer [for-frag]]
            [app.front.view.admin.common :as admin-common]
            [cljs.pprint]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.components :as components]
            [core.front.date-util :as date-util]
            [core.front.href :as href]
            [core.front.icons :as i]
            [core.user.model :as user-model]
            [core.util.util :as util]
            [reagent.core :as r]))


(defn lookup-link
  "Makes a link to look up an entity id (or other search term).
   The link is disabled if the current view is already looking at the entity
   (helps to stop accidentally filling the browser history with the same URL over and over)

   Optional keys for opts:
   `:key` (reagent key for the link if it is part of a collection)

   Otherwise opts is passed onto the link or text element"
  ([search-term]
   (lookup-link {} search-term))
  ([opts search-term]
   (lookup-link opts search-term nil))
  ([opts search-term text]
   (let [search-term (str search-term)]
     [:a (merge opts
                {:href (href/href :view/admin.entity-lookup-with-param
                                  (util/mmap search-term))})
      (or text search-term)])))


(defn expandable-text
  [id text line-count]
  (r/with-let [*open? (r/atom false)]
    [:div.font-normal {:key id}
     [:button.bg-white.border.border-1.border-solid.border-gray-800.text-gray-800
      {:on-click #(swap! *open? not)}
      (if @*open? "Collapse" "Expand") " (" line-count " lines)"]
     [:div.mt-1.whitespace-pre
      {:class (if @*open? "block" "hidden")}
      text]]))


(defn wrap-text
  "Puts the given text into a collapsible section if it is too large"
  [id text]
  (let [line-count (->> text (re-seq #"\n") count)]
    (if (< 15 line-count)
      [expandable-text (str id "/exandable") text line-count]
      [:span.whitespace-pre {:key id} text])))

(def max-coll-items 7)

(defn- format-coll
  [prefix suffix id link children]
  [:span {:key (str id "/coll")}
   prefix
   (when-not (empty? children)
     [:span (->> children
                 (take max-coll-items)
                 (interpose ", ")
                 doall
                 (cons :<>)
                 vec)])
   (when (some? link)
     [:span
      ", "
      (when (< max-coll-items (count children))
        "..., ")
      (lookup-link {} link "View all")])
   suffix])


(defn- make-collection-lookup-link
  "Forms a search term that will look up all the entities in the given collection, if it is one"
  [coll attribute entity-id]
  (when (and (coll? coll)
             (< 1 (count coll))
             (every? admin-common/entity-id? coll))
    (str "[" (str/replace (str attribute) #"/" "/_") " " entity-id "]")))


(defn- map-like?
  "Returns true if the value is a map or something that should be formatted like a map.
   Tailored to vaahtera db schema so a bit hacky and not universal"
  [value]
  (or (map? value)
      (and (coll? value)
           (some map? value))))

(defn format-instant [value]
  [:div.w-full.flex.items-center.justify-between
   [:span
    (date-util/format-technical-date-time value)
    (str " (" (.toISOString value) ")")]
   [:div
    [:a {:href   (str "/api/command/admin/open-gcp-logs?ts=" (.getTime value))
         :target "_blank"}
     "Logs"]
    [:button.ml-2.my-1 {:on-click #(clipboard/copy-text (.toISOString value))} "Copy"]]])


(defn format-map-like [id value]
  [:div.flex.flex-col.gap-1
   (if (sequential? value)
     (for-frag [v value]
       (format-map-like (:db/id v) v))
     [:div.border.border-solid.border-ui-dark-gray.rounded.p-1
      (when-let [ent-id (:db/id value)]
        [:div.pb-1
         (lookup-link {:key id} ent-id)])
      [wrap-text id (with-out-str (cljs.pprint/pprint value))]])])


(defn format-value
  "Formats an entity value recursively so data types are displayed properly
   and entity ids function as links to a lookup of that entity.

   Collections composing only of entity links include a link for looking up all the entities
   in that collection.

   Unlike other collections, maps are displayed as pretty printed strings;
   they are very rare as datomic values and often deeply nested and simple EDN data (see :process/states)
   so it is not worth the performance and indentation headaches to handle them recursively"
  [id entity attribute value]
  (let [coll-fn   (partial map-indexed #(format-value (str id "/coll/" %1) entity attribute %2))
        coll-link (make-collection-lookup-link value attribute entity)]
    (condp #(%1 %2) value
      map-like?               (format-map-like id value)
      set?                    (format-coll "#{" "}" id coll-link (coll-fn (sort value))) ;; Sort maintains set order
      vector?                 (format-coll "[" "]" id coll-link (coll-fn value))
      list?                   (format-coll "(" ")" id coll-link (coll-fn value))
      inst?                   (format-instant value)
      admin-common/entity-id? (lookup-link {:key id} value)
      string?                 [wrap-text id (str "\"" (str/replace value #"\n" "\n ") "\"")]
      nil?                    [:span {:key id} "nil"]
      [:span {:key id} (str value)])))


(defn- entity-table-cell-added? [added?]
  (when (some? added?)
    [:div.tx-change-entity-id
     (cc/icon {:icon        (if added?
                              i/plus
                              i/circle-minus)
               :description (if added? "Added" "Retracted")
               :class-name  "pt-1"
               :fill        (if added?
                              "green"
                              "red")})]))


(defn- entity-table-cell-entity [entity-id]
  (when (some? entity-id)
    [:div.entity-attribute-value (lookup-link entity-id)]))


(defn- entity-table-cell-attribute [attribute]
  (when (some? attribute)
    [:div.entity-attribute-value (str attribute)]))


(defn- entity-table-cell-value [id entity attribute value]
  (when (some? value)
    [:div.entity-attribute-value.overflow-x-auto (format-value id entity attribute value)]))


(defn- entity-table-cell-tx [tx entity attribute]
  (when (some? tx)
    (if (empty? tx)
      [:div.entity-attribute-value "-"]
      (let [id        (-> tx :db/id)
            user      (-> tx :audit/user)
            migration (-> tx :stork/installed-migrations)
            doc       (-> tx :db/doc)
            timestamp (-> tx :db/txInstant (date-util/format-technical-date-time true))]
        [:div.entity-attribute-value.justify-right.whitespace-pre.flex.flex-wrap
         [:div.flex
          [lookup-link {:class-name "flex mr-2"} id timestamp]
          [lookup-link {:class-name "flex mr-2"} (str "[" entity " " attribute "]") "History"]
          (or (when (some? user)
                [lookup-link {:class-name "text-black"} (:db/id user) (user-model/full-name user)])
              (when (some? migration) (str migration))
              doc
              "System")]]))))


(defn- entity-table-header [datoms hidden-keys]
  (let [sample-row (first datoms)
        make-cell  #(when (and (contains? sample-row %1)
                               (not (contains? hidden-keys %1)))
                      %2)]
    (->> [(make-cell :added? "")
          (make-cell :entity "Entity")
          (make-cell :attribute "Attribute")
          (make-cell :value "Value")
          (make-cell :tx "Tx")]
         (remove nil?))))


(defn- preprocess-datoms [datoms]
  (->> datoms
       ;; add background variation by entity
       (group-by :entity)
       (sort-by key)
       (map val)
       (map-indexed (fn [ix datoms]
                      (->> datoms
                           (map #(assoc % :odd? (odd? ix))))))
       (apply concat)
       (sort-by (juxt :entity :attribute :added? :value))
       (map-indexed (fn [index datom] (assoc datom :index index)))))


(defn- entity-table-body [id datoms hidden-keys]
  (for [datom (preprocess-datoms datoms) ; Not for-frag (body for carbon list)
        :let  [{:keys [added? odd? entity attribute value tx index]} (reduce dissoc datom hidden-keys)
               row-key (str "entity-table-" id "-" index)
               val-key (str row-key "-value")]]
    {:key   row-key
     :opts  {:class-name (cond
                           (and odd? (true? added?))  "bg-gray-200"
                           (and odd? (false? added?)) "bg-red-200"
                           (false? added?)            "bg-red-100"
                           :else                      "")}
     :cells (->> [(entity-table-cell-added? added?)
                  (entity-table-cell-entity entity)
                  (entity-table-cell-attribute attribute)
                  (entity-table-cell-value val-key (:entity datom) (:attribute datom) value)
                  (entity-table-cell-tx tx (:entity datom) attribute)]
                 (remove nil?))}))


(defn entity-table
  "Displays the given facts in a formatted table with entity lookup links.
   The required keys are `:id`, `:title` and `:datoms`
   Datoms are taken as a map with the following format:
   {:entity    (entity id)
    :attribute (attribute name)
    :value     (attribute value)
    :added?    (boolean, true if the datom was added as part of a transaction)
    :tx        (the transaction entity id)}
   The `added?` and `tx` keys are optional; the rest are required;
   however, `hidden` is an optional set of the datom keys that should not be displayed."
  [{:keys [id title datoms hidden]}]
  (when (not-empty datoms)
    [components/structured-list-keyed
     {:wrapper-opts {:aria-label title
                     :class-name "bx--structured-list-tight"}}
     (entity-table-header datoms hidden)
     (entity-table-body id datoms hidden)]))
